import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Calendar, Download, PhoneCall, Signal, Sim } from "@/assets/icons/ds";
import { Button } from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { MetricTiles } from "@/components/Layout/MetricTiles/MetricTiles";
import { SubscriptionInfoBox } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionInfoBox/SubscriptionInfoBox";
import {
  useGetFlatPricesAndName,
  useIsMobileAbo,
} from "@/hooks/useDataContextHooks";
import { DataContext } from "@/provider/DataContextProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { ServiceIDs, TOTAL_DAYS_IN_YEAR } from "@/utils/constants";
import { getEntry, hasBasketNumberPorting } from "@/utils/dataContextHelpers";
import { getTariffType, TariffTypes } from "@/utils/deviceUtils";
import { getProductSpeed } from "@/utils/productMatrix";
import {
  CheckoutTariffFeatureRow,
  getCheckoutTariffFeatureGroups,
  useHasPrechargedMobileAbo,
} from "@/utils/tariffUtils";

import { DeviceDetailsForm } from "./DeviceDetailsForm/DeviceDetailsForm";

interface Props {
  isFlatHome?: boolean;
  showErrors?: boolean;
  /** Customer has CreditClassC and needs a Swiss ID document to use roaming calls. */
  isInternationalCallsRestricted?: boolean;
}

const featureRowIcon: Record<CheckoutTariffFeatureRow["icon"], typeof Signal> =
  {
    signal: Signal,
    phone: PhoneCall,
  };

const TariffFeatureGroup: FC<{
  title: string;
  description?: string;
  rows: CheckoutTariffFeatureRow[];
}> = ({ title, description, rows }) => (
  <div className="flex w-full flex-col items-start gap-2 rounded-md bg-surface-quaternary p-4">
    <div className="flex w-full flex-col gap-1">
      <p className="text-body-sm font-semibold text-foreground-secondary">
        {title}
      </p>
      {description && (
        <p
          data-testid="international-restriction-hint"
          className="text-body-xs leading-none text-foreground-secondary"
        >
          {description}
        </p>
      )}
    </div>
    {rows.map((row) => {
      const Icon = featureRowIcon[row.icon];

      return (
        <div key={row.title} className="flex w-full items-center gap-2">
          <Icon className="size-6 shrink-0 text-foreground-primary" />
          <div className="flex flex-1 flex-col gap-1 leading-none">
            <p className="text-body-base font-semibold text-foreground-primary">
              {row.title}
            </p>
            <p className="text-body-xs text-foreground-secondary">
              {row.subtitle}
            </p>
          </div>
        </div>
      );
    })}
  </div>
);

/**
 * This component should implement the device assignment
 * and the rate picker during the onboarding.
 */
export const Summary: FC<Props> = ({
  isFlatHome,
  showErrors,
  isInternationalCallsRestricted,
}) => {
  const { dataContext } = useContext(DataContext);
  const navigate = useNavigate();
  const { t } = useTranslation("translation", {
    keyPrefix: "Onboarding.sections.checkout.summary",
  });
  const { t: tUnscoped } = useTranslation();

  const entry = getEntry(dataContext);

  const { flatName, bandwidthLabel, minContractDur } =
    useGetFlatPricesAndName();
  const isMobileAbo = useIsMobileAbo();
  const hasPrechargedMobileAbo = useHasPrechargedMobileAbo(dataContext.basket);
  const isPrecharged =
    !!dataContext.selectedOption?.precharged || hasPrechargedMobileAbo;
  const isPurchaseIntent =
    dataContext.wizardEntry?.source === "purchase-intent";
  const serviceId = entry?.options?.[0]?.product?.id ?? entry?.product?.id;
  const tariffType = getTariffType(serviceId);
  const isDataOnlyTariff = tariffType === TariffTypes.dataTariff;
  const canChangeRate = !isFlatHome && !isPrecharged && !isPurchaseIntent;
  const hasONP = hasBasketNumberPorting(entry, dataContext.numberPorting);
  // The backend may have already persisted the porting number (mnpData) even
  // when the in-memory form state (dataContext.numberPorting) is gone, e.g.
  // after a reload landing straight back on checkout.
  const portedNumber =
    dataContext.numberPorting?.currentNumber ??
    entry?.telcoData?.mnpData?.msisdn;

  // BasketEntry only carries a LookupValue (id/name) for the product, not the
  // full Product with minContractDur, so the Month/Year service id decides it
  // - unless useGetFlatPricesAndName already resolved the real one.
  const minContractDuration =
    minContractDur ??
    (serviceId === ServiceIDs.FlatMobileYear ? TOTAL_DAYS_IN_YEAR : 30);
  const tariffFeatureGroups =
    !isFlatHome && !isDataOnlyTariff
      ? getCheckoutTariffFeatureGroups(
          tUnscoped,
          tariffType,
          minContractDuration,
        )
      : undefined;

  // Flat Home is sold with a fixed speed pair per generation - same numbers
  // `getSubscriptionSpeeds` uses post-purchase in "Dein Abo"
  // (SubscriptionOptions.tsx), just read here from the product matrix instead
  // of a provisioned SIM. A data-only tariff instead states its download
  // speed directly (`bandwidthLabel`); upload is estimated as half of it,
  // the same approximation `getDataSpeedSummaryForRateSelection` and
  // `getSubscriptionSpeeds` already use for that gap.
  const dataOnlyDownload = isDataOnlyTariff ? Number(bandwidthLabel) : NaN;
  const speeds = isFlatHome
    ? getProductSpeed(serviceId)
    : isDataOnlyTariff &&
        !Number.isNaN(dataOnlyDownload) &&
        dataOnlyDownload > 0
      ? { download: dataOnlyDownload, upload: dataOnlyDownload / 2 }
      : undefined;
  const network = isFlatHome
    ? serviceId === ServiceIDs.FlatHome4G
      ? "4G"
      : "5G"
    : "5G";
  // Contract term is only shown for Flat Home (minimum term of 12 months).
  const durationLabel = isFlatHome
    ? tUnscoped("portal:flatHome.duration")
    : undefined;
  // Data-only tariffs fall back to the plain name (like a regular mobile abo)
  // if bandwidthLabel didn't resolve - Flat Home's speed pair is a fixed
  // catalog lookup by id, so it's never in that position.
  const showSpeedTiles = isFlatHome || (isDataOnlyTariff && !!speeds);

  return (
    <ProfileCard
      variant="white"
      bodyGap={6}
      className="payment-summary"
      title={t("title")}
    >
      <DeviceDetailsForm showErrors={showErrors} />

      <div className="flex w-full flex-col gap-3">
        <div className="flex w-full items-center justify-between">
          <p className="text-body-base font-semibold leading-none tracking-[0.32px] text-foreground-primary">
            {t("rateOptions")}
          </p>
          {canChangeRate && (
            <Button
              dataTestid="change-rate-button"
              variant="primary"
              size="sm"
              onClick={() => navigate(portalRoutes.addDevice.rateSelection)}
            >
              {t("changeRate")}
            </Button>
          )}
        </div>
        {!showSpeedTiles && (
          <div className="flex w-full flex-col gap-2">
            <SubscriptionInfoBox className="flex items-center gap-3">
              <Sim className="size-6 shrink-0 text-foreground-primary" />
              <p className="text-body-lg font-semibold leading-md text-foreground-primary">
                {flatName}
              </p>
            </SubscriptionInfoBox>
            {tariffFeatureGroups && (
              <>
                <TariffFeatureGroup
                  title={t("switzerlandHeading")}
                  rows={[tariffFeatureGroups.switzerland]}
                />
                {tariffFeatureGroups.international.length > 0 && (
                  <TariffFeatureGroup
                    title={t("internationalHeading")}
                    description={
                      isInternationalCallsRestricted
                        ? t("internationalRestriction")
                        : undefined
                    }
                    rows={tariffFeatureGroups.international}
                  />
                )}
                {isMobileAbo && (
                  <Notification
                    dataTestid="number-porting-hint"
                    title={
                      hasONP
                        ? (portedNumber ?? t("portedNumberTitle"))
                        : t("newNumberTitle")
                    }
                    description={
                      hasONP ? t("portedNumberHint") : t("newNumberHint")
                    }
                  />
                )}
              </>
            )}
          </div>
        )}
        {showSpeedTiles && speeds && (
          <div className="flex w-full flex-col gap-2">
            <SubscriptionInfoBox className="flex items-center gap-3">
              <Sim className="size-6 shrink-0 text-foreground-primary" />
              <p className="text-body-lg font-semibold leading-md text-foreground-primary">
                {flatName}
              </p>
            </SubscriptionInfoBox>
            <MetricTiles
              metrics={[
                {
                  icon: <Download className="size-10" />,
                  value: `${speeds.download}`,
                  unit: tUnscoped(
                    "portal:cockpit.managementTile.subscription.options.maxSpeedUnit",
                  ),
                  label: tUnscoped(
                    "portal:cockpit.managementTile.subscription.options.download",
                  ),
                },
                {
                  icon: <Download className="size-10 rotate-180" />,
                  value: `${speeds.upload}`,
                  unit: tUnscoped(
                    "portal:cockpit.managementTile.subscription.options.maxSpeedUnit",
                  ),
                  label: tUnscoped(
                    "portal:cockpit.managementTile.subscription.options.upload",
                  ),
                },
                {
                  icon: <Signal className="size-6" />,
                  value: network,
                  unit: tUnscoped(
                    "portal:cockpit.managementTile.subscription.options.unlimited",
                  ),
                },
              ]}
            />
            {isFlatHome && durationLabel && (
              <SubscriptionInfoBox className="flex items-center gap-3">
                <Calendar className="size-6 shrink-0 text-foreground-primary" />
                <p className="text-body-lg font-semibold leading-md text-foreground-primary">
                  {durationLabel}
                </p>
              </SubscriptionInfoBox>
            )}
          </div>
        )}
      </div>
    </ProfileCard>
  );
};
